import axios from '@axios'

const SUMMARY_BATCH_SIZE = 100
let queuedSummaryIds = new Set()
let summaryTimer = null

const state = () => ({
  panel: { open: false, quoteId: null, folio: null },
  summaries: {},
  drafts: {},
})

const getters = {
  panel: state => state.panel,
  summaryFor: state => quoteId => state.summaries[quoteId] || null,
  draftFor: state => quoteId => state.drafts[quoteId] || '',
}

const mutations = {
  OPEN_PANEL(state, { quoteId, folio = null }) {
    state.panel = { open: true, quoteId, folio }
  },
  CLOSE_PANEL(state) {
    state.panel = { ...state.panel, open: false }
  },
  SET_PANEL_FOLIO(state, folio) {
    state.panel = { ...state.panel, folio }
  },
  MERGE_SUMMARIES(state, { quoteIds, data }) {
    const next = { ...state.summaries }
    quoteIds.forEach(id => {
      next[id] = data[id] || { count: 0, last: null }
    })
    state.summaries = next
  },
  SET_SUMMARY(state, { quoteId, summary }) {
    state.summaries = { ...state.summaries, [quoteId]: summary }
  },
  SET_DRAFT(state, { quoteId, text }) {
    const next = { ...state.drafts }
    if (text) next[quoteId] = text
    else delete next[quoteId]
    state.drafts = next
  },
}

const summaryFromTimeline = entries => {
  const notes = entries.filter(entry => entry.kind === 'note')
  const [last] = notes
  return {
    count: notes.length,
    last: last ? { body: last.body, author_name: last.author?.name, created_at: last.created_at } : null,
  }
}

const actions = {
  async fetchQuoteTimeline({ commit }, quoteId) {
    const { data } = await axios.get(`/v1/quotes/${quoteId}/notes`, { skipErrorToast: true })
    commit('SET_SUMMARY', { quoteId, summary: summaryFromTimeline(data.data) })
    return data
  },

  async createQuoteNote(_, { quoteId, body }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/notes`, { quote_note: { body } }, { skipErrorToast: true })
    return data
  },

  async updateQuoteNote(_, { quoteId, noteId, body }) {
    const { data } = await axios.patch(`/v1/quotes/${quoteId}/notes/${noteId}`, { quote_note: { body } }, { skipErrorToast: true })
    return data
  },

  async deleteQuoteNote(_, { quoteId, noteId }) {
    const { data } = await axios.delete(`/v1/quotes/${quoteId}/notes/${noteId}`, { skipErrorToast: true })
    return data
  },

  async fetchQuoteNotesSummary({ commit }, quoteIds) {
    const ids = [...new Set((quoteIds || []).filter(Boolean))]
    if (!ids.length) return {}
    const { data } = await axios.get('/v1/quote_notes/summary', { params: { quote_ids: ids }, skipErrorToast: true })
    commit('MERGE_SUMMARIES', { quoteIds: ids, data: data.data || {} })
    return data.data
  },

  queueQuoteNotesSummary({ dispatch }, quoteId) {
    if (!quoteId) return
    queuedSummaryIds.add(quoteId)
    if (summaryTimer) return
    summaryTimer = setTimeout(() => {
      const ids = [...queuedSummaryIds]
      queuedSummaryIds = new Set()
      summaryTimer = null
      for (let start = 0; start < ids.length; start += SUMMARY_BATCH_SIZE) {
        dispatch('fetchQuoteNotesSummary', ids.slice(start, start + SUMMARY_BATCH_SIZE)).catch(() => {})
      }
    }, 50)
  },

  syncSummaryFromTimeline({ commit }, { quoteId, entries }) {
    commit('SET_SUMMARY', { quoteId, summary: summaryFromTimeline(entries) })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
